import React, { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  FaWallet,
  FaExchangeAlt,
  FaChartLine,
  FaArrowRight,
} from "react-icons/fa";
import BorderRayCircuit from "../BorderRay/borderRay";
import "../BorderRay/borderRay.css";

const services = [
  {
    icon: <FaWallet />,
    title: "Wallet",
    link: "wallet.bingold.to",
    url: "https://chromewebstore.google.com/detail/bingold/eaedaahjihfhckhgpekcahccdgceeneh",
    description: "Self-custodial storage for BIGOD, crypto and NFTs.",
  },
  {
    icon: <FaExchangeAlt />,
    title: "Bridge",
    link: "bridge.bingold.to",
    url: "https://bridge.bingold.to/",
    description: "Transfer BIGOD across BNB Chain and connected networks.",
  },
  {
    icon: <FaChartLine />,
    title: "Trade",
    link: "trade.bingold.to",
    url: "https://trade.bingold.to/login",
    description: "Buy, sell and settle BIGOD in fiat and crypto.",
  },
];

const Services = () => {
  useEffect(() => {
    AOS.init({
      duration: 900,
      once: true,
      easing: "ease-in-out",
    });
  }, []);

  return (
    <section
      className="relative overflow-hidden bg-[#050b14] text-white font-semibold py-20 px-6"
      style={{ fontFamily: '"Montserrat", sans-serif' }}
    >
      <style>{`
        /* ---------- Floating gradient blobs ---------- */
        @keyframes floatTopLeft {
          0%   { transform: translate(0px, 0px) scale(1); }
          33%  { transform: translate(60px, 40px) scale(1.1); }
          66%  { transform: translate(-30px, 70px) scale(0.95); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        @keyframes floatTopRight {
          0%   { transform: translate(0px, 0px) scale(1); }
          33%  { transform: translate(-50px, 60px) scale(1.05); }
          66%  { transform: translate(30px, -40px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        @keyframes floatBottomLeft {
          0%   { transform: translate(0px, 0px) scale(1); }
          33%  { transform: translate(50px, -50px) scale(1.1); }
          66%  { transform: translate(-40px, -20px) scale(0.95); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        @keyframes floatBottomRight {
          0%   { transform: translate(0px, 0px) scale(1); }
          33%  { transform: translate(-60px, -30px) scale(1.08); }
          66%  { transform: translate(20px, -60px) scale(0.92); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .float-top-left { animation: floatTopLeft 14s ease-in-out infinite; }
        .float-top-right { animation: floatTopRight 16s ease-in-out infinite; }
        .float-bottom-left { animation: floatBottomLeft 18s ease-in-out infinite; }
        .float-bottom-right { animation: floatBottomRight 15s ease-in-out infinite; }

        /* ---------- Wave layer (aurora-like moving gradient) ---------- */
        @keyframes waveDrift {
          0%   { transform: translateX(-10%) translateY(0px) rotate(0deg); }
          50%  { transform: translateX(10%) translateY(-20px) rotate(2deg); }
          100% { transform: translateX(-10%) translateY(0px) rotate(0deg); }
        }
        @keyframes waveDrift2 {
          0%   { transform: translateX(10%) translateY(0px) rotate(0deg); }
          50%  { transform: translateX(-15%) translateY(15px) rotate(-2deg); }
          100% { transform: translateX(10%) translateY(0px) rotate(0deg); }
        }
        .wave-layer-1 {
          animation: waveDrift 20s ease-in-out infinite;
        }
        .wave-layer-2 {
          animation: waveDrift2 24s ease-in-out infinite;
        }

        /* ---------- Lightning / shimmer sweep ---------- */
        @keyframes lightningSweep {
          0%   { transform: translateX(-150%) skewX(-20deg); opacity: 0; }
          8%   { opacity: 0.55; }
          16%  { opacity: 0; }
          100% { transform: translateX(250%) skewX(-20deg); opacity: 0; }
        }
        .lightning-sweep {
          animation: lightningSweep 7s ease-in-out infinite;
        }

        /* ---------- Traveling glow along the timeline connector ---------- */
        @keyframes travelGlow {
          0%   { left: 0%; opacity: 0; }
          8%   { opacity: 1; }
          92%  { opacity: 1; }
          100% { left: 100%; opacity: 0; }
        }
        .travel-glow {
          animation: travelGlow 4.5s linear infinite;
        }

        /* ---------- Subtle bobbing for circles ---------- */
        @keyframes softBob {
          0%, 100% { transform: translateY(0px); }
          50%      { transform: translateY(-6px); }
        }
        .soft-bob {
          animation: softBob 4s ease-in-out infinite;
        }

        /* ---------- Card border glow pulse ---------- */
        @keyframes borderPulse {
          0%, 100% { box-shadow: 0 0 0px rgba(34,211,238,0); }
          50%      { box-shadow: 0 0 35px rgba(34,211,238,0.15); }
        }
        .card-pulse {
          animation: borderPulse 5s ease-in-out infinite;
        }

        /* ---------- Twinkling stars/particles ---------- */
        @keyframes twinkle {
          0%, 100% { opacity: 0.15; transform: scale(1); }
          50%      { opacity: 0.8; transform: scale(1.4); }
        }
        .twinkle {
          animation: twinkle 3s ease-in-out infinite;
        }
      `}</style>

      {/* ================= Animated Background ================= */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Base gradient */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,#12365f_0%,#091726_40%,#050b14_100%)]"></div>

        {/* Wave / aurora layers */}
        <div className="wave-layer-1 absolute -top-1/3 left-0 w-[140%] h-[70%] bg-[radial-gradient(ellipse_at_center,rgba(34,211,238,0.10)_0%,transparent_70%)] blur-3xl"></div>
        <div className="wave-layer-2 absolute -bottom-1/3 right-0 w-[140%] h-[70%] bg-[radial-gradient(ellipse_at_center,rgba(234,179,8,0.08)_0%,transparent_70%)] blur-3xl"></div>

        {/* Floating blobs */}
        <div className="float-top-left absolute -top-40 -left-32 w-[500px] h-[500px] rounded-full bg-cyan-500/10 blur-[150px]"></div>
        <div className="float-top-right absolute -top-32 -right-32 w-[450px] h-[450px] rounded-full bg-yellow-500/10 blur-[150px]"></div>
        <div className="float-bottom-left absolute -bottom-40 -left-24 w-[450px] h-[450px] rounded-full bg-cyan-500/10 blur-[150px]"></div>
        <div className="float-bottom-right absolute -bottom-40 -right-24 w-[500px] h-[500px] rounded-full bg-yellow-500/10 blur-[150px]"></div>

        {/* Lightning sweep beam */}
        <div className="lightning-sweep absolute top-0 left-0 w-[35%] h-full bg-gradient-to-r from-transparent via-cyan-300/20 to-transparent"></div>

        {/* Twinkling particles */}
        <div className="twinkle absolute top-[15%] left-[20%] w-1 h-1 rounded-full bg-cyan-300"></div>
        <div
          className="twinkle absolute top-[65%] left-[35%] w-1 h-1 rounded-full bg-yellow-300"
          style={{ animationDelay: "1s" }}
        ></div>
        <div
          className="twinkle absolute top-[30%] left-[70%] w-1.5 h-1.5 rounded-full bg-cyan-300"
          style={{ animationDelay: "2s" }}
        ></div>
        <div
          className="twinkle absolute top-[75%] left-[80%] w-1 h-1 rounded-full bg-yellow-300"
          style={{ animationDelay: "0.5s" }}
        ></div>
        <div
          className="twinkle absolute top-[45%] left-[10%] w-1 h-1 rounded-full bg-cyan-300"
          style={{ animationDelay: "1.5s" }}
        ></div>

        {/* Subtle grid texture */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        ></div>
      </div>

      {/* ================= Main Content ================= */}
      <div className="relative z-10 max-w-6xl mx-auto">
        {/* Hero */}
        {/* Hero */}
        <div className="max-w-2xl mx-auto mb-16 text-center" data-aos="fade-up">
          <div className="flex items-center justify-center gap-3 mb-6">
            {/* <span className="w-8 h-px bg-cyan-500"></span> */}
            <p className="text-cyan-400 md:text-3xl text-2xl font-semibold  z-10">
              Our Services
            </p>
          </div>

          <h1 className="text-lg md:text-2xl leading-none text-white">
            One token.
          </h1>

          <h2 className="text-lg md:text-2xl leading-none text-cyan-400 mt-3">
            Three ways to hold it.
          </h2>

          <p className="text-white font-semibold text-[15px] leading-8 mt-6 max-w-xl mx-auto">
            Wallet, bridge and trade — the infrastructure behind BIGOD, a fully
            auditable gold-backed token on BNB Chain.
          </p>
          <div className="flex justify-center mt-5">
            <BorderRayCircuit />
          </div>
        </div>

        {/* ================= Services Timeline ================= */}
        <div className="relative mt-8">
          {/* Top Horizontal Line with traveling glow */}
          <div className="hidden md:block absolute top-8 left-12 right-12 h-px bg-cyan-500/20 overflow-visible">
            <div className="travel-glow absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-cyan-300 shadow-[0_0_15px_4px_rgba(34,211,238,0.6)]"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((item, index) => (
              <div
                key={index}
                data-aos="fade-up"
                data-aos-delay={index * 150}
                className="relative text-center group rounded-2xl border border-white/5 bg-white/[0.02] backdrop-blur-sm px-6 py-8 card-pulse transition-all duration-500 hover:border-cyan-500/30 hover:bg-white/[0.04]"
              >
                {/* Circle */}
                <div className="soft-bob relative z-10 mx-auto w-12 h-12 rounded-full border border-cyan-500 bg-[#050b14] flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:shadow-[0_0_25px_rgba(234,179,8,.4)]">
                  <span className="text-cyan-400 text-2xl ">
                    {item.title.charAt(0)}
                  </span>
                </div>

                <h3 className="mt-3 text-[26px]  text-cyan-400">
                  {item.title}
                </h3>

                {/* URL */}
                <a
                  href={`${item.url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-block text-[14px] font-normal text-white hover:text-cyan-400 transition-colors duration-300"
                >
                  {item.link}
                </a>

                {/* Description */}
                <p className="mt-1 text-[15px] leading-2 text-white font-semibold max-w-xs mx-auto">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
